import {useCallback, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {getCityMapRegionParams, getCityOption} from "../../../../../config/cities";
import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {getRecentQuartersNames, lastFinishedQuarter, originDate} from "../../../../../utils/dates";
import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {mapPathnameToOfferType, OfferType} from "../../../common/app/routing/offer_type";
import {LineColumnChartCard} from "../../../components/charts/chart_cards/LineColumnChartCard";
import {MultilineChartCard} from "../../../components/charts/chart_cards/MultilineChartCard";
import {PieBarChartCard} from "../../../components/charts/chart_cards/PieBarChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {
    clearChartsRegionPoc,
    selectChartsSelectedRegion,
    setChartsSelectedRegion
} from "../../../components/map/redux/map_city_poc_slice";
import {IOsmDistrictPolygon} from "../../../components/map/types/osm_district_polygon";
import {
    buildDistrictDrillDownSelection,
    buildMapParentRegionSnapshot,
    restoreParentRegionSelection
} from "../../../components/map/utils/district_map_drill_down";
import {toCityOutlineOsmMapParams} from "../../../components/map/utils/osm_map_region_params";
import {selectAuthStatus} from "../../auth/auth_slice";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {
    useGetChartsMonthCityStatsQuery,
    useGetChartsMonthCityTypeStatsQuery,
    useGetChartsMonthDistrictStatsQuery,
    useGetChartsQuarterCityStatsQuery,
    useGetChartsQuarterCityTypeStatsQuery
} from "../api/charts_platform_api";
import {DistrictOsmMapCard} from "../components/DistrictOsmMapCard";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useEffectiveCitySlug} from "../hooks/use_effective_city_slug";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {buildDistrictMapValuesBySlug, resolveMapMetric} from "../utils/build_district_map_values_by_slug";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

export const Prices = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const citySlug = useEffectiveCitySlug();
        const {isKnownCity, regionLabel} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        usePageview(() => {
            pageviewAlgolyticsHit({citySlug, regionType, viewType: ViewType.PRICE_DETAILS});
        });
        const checkOfferType = mapPathnameToOfferType(path);
        const isFlatOfferType = checkOfferType === OfferType.FLAT;
        const {t, i18n} = useTranslation();
        const {currentDate} = useAppSelector(selectLatestEntry);
        const lastMonth = currentDate ?? "";
        const lastQuarter = lastFinishedQuarter(lastMonth);
        const {isLoggedIn, isAuthorized} = useAppSelector(selectAuthStatus);
        const shouldFetch = isLoggedIn && isAuthorized;
        const dispatch = useAppDispatch();
        const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
        const {regionId, districtChartsRegionId, effectiveCitySlug, hasChartsAccess, isRegionIdReady} =
            useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;
        const chartsCitySlug = effectiveCitySlug ?? citySlug;
        const isAgglomerationView = getCityOption(chartsCitySlug).useAgglomerationApi;
        const routeMapParams = useMemo(
            () => getCityMapRegionParams(chartsCitySlug, regionId ?? undefined),
            [chartsCitySlug, regionId]
        );
        const baseOsmMapParams = useMemo(
            () =>
                chartsSelectedRegion != null
                    ? {
                          regionsMapSlug: chartsSelectedRegion.regionsMapSlug,
                          regionsMapParentId: chartsSelectedRegion.regionsMapParentId,
                          regionsMapRegionId: chartsSelectedRegion.regionsMapRegionId,
                          regionsMapChildType: chartsSelectedRegion.regionsMapChildType
                      }
                    : routeMapParams,
            [chartsSelectedRegion, routeMapParams]
        );
        const hasDistrictStatsMap = baseOsmMapParams.regionsMapParentId != null;
        const skipChartsFetch = !shouldFetch || !isRegionIdReady || regionId == null;

        const {
            data = [],
            isError,
            isSuccess
        } = useGetChartsMonthCityStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                scenario: "prices"
            },
            {skip: skipChartsFetch}
        );

        const {
            data: typeData = [],
            isError: isTypeDataError,
            isSuccess: isTypeDataSuccess
        } = useGetChartsMonthCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                offer_type: "Mieszkania",
                scenario: "prices"
            },
            {skip: skipChartsFetch}
        );

        const {
            data: quarterData = [],
            isError: isQuarterDataError,
            isSuccess: isQuarterDataSuccess
        } = useGetChartsQuarterCityStatsQuery(
            {
                region_id: regionId!,
                date_start: lastQuarter,
                date_end: lastQuarter,
                scenario: "prices"
            },
            {skip: skipChartsFetch}
        );

        const {
            data: quarterTypeData = [],
            isError: isQuarterTypeDataError,
            isSuccess: isQuarterTypeDataSuccess
        } = useGetChartsQuarterCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: lastQuarter,
                date_end: lastQuarter,
                scenario: "prices",
                ...(isFlatOfferType ? {offer_type: "Mieszkania"} : {})
            },
            {skip: skipChartsFetch}
        );

        const {data: monthDistrictData = []} = useGetChartsMonthDistrictStatsQuery(
            {
                region_id: districtChartsRegionId!,
                date_start: lastMonth,
                date_end: lastMonth,
                scenario: "prices",
                ...(isFlatOfferType ? {offer_type: "Mieszkania"} : {})
            },
            {
                skip: skipChartsFetch || districtChartsRegionId == null || (isAgglomerationView && !hasDistrictStatsMap)
            }
        );

        const isCityMultiDistrictView = baseOsmMapParams.regionsMapParentId != null;
        const showDistrictPolygons = isCityMultiDistrictView && monthDistrictData.length > 0;
        const osmMapParams = useMemo(
            () => (showDistrictPolygons ? baseOsmMapParams : toCityOutlineOsmMapParams(baseOsmMapParams)),
            [baseOsmMapParams, showDistrictPolygons]
        );

        const monthStatsData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
        const monthTypeStatsData = useMemo(() => (Array.isArray(typeData) ? typeData : []), [typeData]);
        const dataStatus = mapRtkQueryStatusToRequestStatus(isError, isSuccess);
        const typeDataStatus = mapRtkQueryStatusToRequestStatus(isTypeDataError, isTypeDataSuccess);
        const quarterStatus = mapRtkQueryStatusToRequestStatus(isQuarterDataError, isQuarterDataSuccess);

        const monthData = monthStatsData.length > 0 ? [monthStatsData[monthStatsData.length - 1]] : [];
        const flatMonthData = monthTypeStatsData.length > 0 ? [monthTypeStatsData[monthTypeStatsData.length - 1]] : [];

        const districtsMapAvailable = Boolean(
            osmMapParams.regionsMapSlug ||
                osmMapParams.regionsMapParentId != null ||
                osmMapParams.regionsMapRegionId != null
        );
        const lastMapStats = isFlatOfferType ? monthTypeStatsData.at(-1) : monthStatsData.at(-1);
        const districtPriceBySlug = useMemo(
            () =>
                buildDistrictMapValuesBySlug({
                    districtsMapAvailable,
                    monthDistrictData,
                    chartsCitySlug,
                    districtMetric: (d) => d.avg_price_m2,
                    cityLevelMetric: resolveMapMetric(lastMapStats?.avg_price_m2),
                    cityLevelStatsRecord: lastMapStats,
                    extraSlugKeys: [osmMapParams.regionsMapSlug, chartsSelectedRegion?.slug]
                }),
            [
                chartsCitySlug,
                chartsSelectedRegion?.slug,
                districtsMapAvailable,
                lastMapStats,
                monthDistrictData,
                osmMapParams.regionsMapSlug
            ]
        );
        const districtDrillDownEnabled = showDistrictPolygons && monthDistrictData.length > 0;
        const districtStatsCitySlug = getCityOption(chartsCitySlug).requestCitySlug;
        const mapParentRegion = chartsSelectedRegion?.parentRegion;

        const handleDistrictPolygonClick = useCallback(
            (district: IOsmDistrictPolygon) => {
                if (
                    districtChartsRegionId == null ||
                    baseOsmMapParams.regionsMapParentId == null ||
                    chartsCitySlug == null
                ) {
                    return;
                }

                const parentLabel = chartsSelectedRegion?.label ?? t(`city.${citySlug}.nominative`);
                const parentSlug = chartsSelectedRegion?.slug ?? baseOsmMapParams.regionsMapSlug ?? "";

                dispatch(
                    setChartsSelectedRegion(
                        buildDistrictDrillDownSelection(
                            district,
                            chartsCitySlug,
                            buildMapParentRegionSnapshot({
                                isRouteCity: chartsSelectedRegion == null,
                                regionId: districtChartsRegionId,
                                slug: parentSlug,
                                label: parentLabel,
                                citySlug: chartsSelectedRegion?.citySlug ?? chartsCitySlug,
                                hasChartsAccess,
                                mapParams: baseOsmMapParams
                            })
                        )
                    )
                );

                window.userengage?.("event.[BD]_district_click", {
                    city: chartsCitySlug,
                    district: district.slug_district ?? district.slug
                });
            },
            [baseOsmMapParams, chartsCitySlug, chartsSelectedRegion, citySlug, dispatch, districtChartsRegionId, t]
        );

        const handleBackToMapParent = useCallback(() => {
            const parent = chartsSelectedRegion?.parentRegion;
            if (!parent) {
                return;
            }

            if (parent.isRouteCity) {
                dispatch(clearChartsRegionPoc());
            } else {
                dispatch(setChartsSelectedRegion(restoreParentRegionSelection(parent)));
            }
        }, [chartsSelectedRegion, dispatch]);
        const pricesMapMetricLabel = t("fields.avg_price_m2", {defaultValue: "Srednia cena za m2"}).replace(
            /m<sup>2<\/sup>/g,
            "m²"
        );
        const quarterLabels = getRecentQuartersNames(new Date(lastQuarter), 5);
        quarterLabels.unshift(`${t("common.before")} ${quarterLabels[0]}`);

        return (
            <Layout>
                <Grid type={districtsMapAvailable ? "subpage" : "subpageMapless"}>
                    <LineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart1"
                        chartId="prices-chart1"
                        title={<Trans>{t("prices.chart1.title")}</Trans>}
                        helper={<Trans>{t("prices.chart1.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        columnFields={["available_offers", "sold", "added"]}
                        lineField="avg_price_m2"
                        tooltipUnits={{
                            column: " ofert",
                            line: ` ${t("common.pln_m2")}`
                        }}
                        colorSet="legendLike"
                        roundValues
                        nullZeroLineValues
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <MultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart2"
                        chartId="prices-chart2"
                        title={<Trans>{t("prices.chart2.title")}</Trans>}
                        helper={<Trans>{t("prices.chart2.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "avg_price_m2_studio",
                            "avg_price_m2_2_rooms",
                            "avg_price_m2_3_rooms",
                            "avg_price_m2_4_plus_rooms",
                            ...(isFlatOfferType ? [] : ["avg_price_m2_house"]),
                            "avg_price_m2"
                        ]}
                        unit={` ${t("common.pln_m2")}`}
                        roundValues
                        nullZeroValues
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <MultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart3"
                        chartId="prices-chart3"
                        title={<Trans>{t("prices.chart3.title")}</Trans>}
                        helper={<Trans>{t("prices.chart3.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "avg_price",
                            "avg_price_studio",
                            "avg_price_2_rooms",
                            "avg_price_3_rooms",
                            "avg_price_4_plus_rooms",
                            ...(isFlatOfferType ? [] : ["avg_price_house"])
                        ]}
                        unit={` ${t("common.pln")}`}
                        roundValues
                        nullZeroValues
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <PieBarChartCard
                        area="chart4"
                        chartId="prices-chart4"
                        title={<Trans>{t("prices.chart4.title")}</Trans>}
                        helper={<Trans>{t("prices.chart4.description")}</Trans>}
                        pieData={isFlatOfferType ? flatMonthData : monthData}
                        pieStatus={dataStatus}
                        pieFields={[
                            "available_offers_delivery_deadline_ready",
                            "available_offers_delivery_deadline_in_3_months",
                            "available_offers_delivery_deadline_in_6_months",
                            "available_offers_delivery_deadline_in_1_year",
                            "available_offers_delivery_deadline_in_2_year",
                            "available_offers_delivery_deadline_above_2_years"
                        ]}
                        pieInnerTitle={t(isFlatOfferType ? "prices.number_of_flats" : "prices.number_of_properties")}
                        barData={isFlatOfferType ? flatMonthData : monthData}
                        barStatus={dataStatus}
                        barFields={[
                            "avg_price_m2_delivery_deadline_ready",
                            "avg_price_m2_delivery_deadline_in_3_months",
                            "avg_price_m2_delivery_deadline_in_6_months",
                            "avg_price_m2_delivery_deadline_in_1_year",
                            "avg_price_m2_delivery_deadline_in_2_year",
                            "avg_price_m2_delivery_deadline_above_2_years"
                        ]}
                        barMulticolorSet="alternate"
                        barUnit={` ${t("common.pln_m2")}`}
                        roundValues
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <MultilineChartCard<IMonthCityStats>
                        area="chart5"
                        chartId="prices-chart5"
                        title={<Trans>{t("prices.chart5.title")}</Trans>}
                        helper={<Trans>{t("prices.chart5.description")}</Trans>}
                        data={monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "avg_price_m2_area_apartments_to_30m2",
                            "avg_price_m2_area_apartments_between_30_and_50m2",
                            "avg_price_m2_area_apartments_between_50_and_70m2",
                            "avg_price_m2_area_apartments_between_70_and_100m2",
                            "avg_price_m2_area_apartments_between_100_and_120m2",
                            "avg_price_m2_area_apartments_above_120m2"
                        ]}
                        unit={` ${t("common.pln_m2")}`}
                        roundValues
                        nullZeroValues
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <MultilineChartCard<IMonthCityStats>
                        area="chart6"
                        chartId="prices-chart6"
                        title={<Trans>{t("prices.chart7.title")}</Trans>}
                        helper={<Trans>{t("prices.chart7.description")}</Trans>}
                        data={monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "avg_price_m2_investment_type_intimate",
                            "avg_price_m2_investment_type_small",
                            "avg_price_m2_investment_type_medium",
                            "avg_price_m2_investment_type_large",
                            "avg_price_m2_investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["avg_price_m2_investment_type_house"])
                        ]}
                        unit={` ${t("common.pln_m2")}`}
                        roundValues
                        nullZeroValues
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    {/*handle one case, we do not have agglomeration data for the type of flat*/}
                    {!(isAgglomerationView && isFlatOfferType) && (
                        <PieBarChartCard
                            area="chart7"
                            chartId="prices-chart7"
                            title={<Trans>{t("prices.chart6.title")}</Trans>}
                            helper={<Trans>{t("prices.chart6.description")}</Trans>}
                            pieData={isFlatOfferType ? quarterTypeData : quarterData}
                            pieStatus={quarterStatus}
                            pieFields={[
                                "available_offers_sale_started_above_4_quarters_ago",
                                "available_offers_sale_started_4_quarters_ago",
                                "available_offers_sale_started_3_quarters_ago",
                                "available_offers_sale_started_2_quarters_ago",
                                "available_offers_sale_started_1_quarter_ago",
                                "available_offers_sale_started_now"
                            ]}
                            pieFieldsLabels={quarterLabels}
                            pieInnerTitle={t(
                                isFlatOfferType ? "prices.number_of_flats" : "prices.number_of_properties"
                            )}
                            barData={isFlatOfferType ? quarterTypeData : quarterData}
                            barStatus={quarterStatus}
                            barFields={[
                                "avg_price_m2_sale_started_above_4_quarters_ago",
                                "avg_price_m2_sale_started_4_quarters_ago",
                                "avg_price_m2_sale_started_3_quarters_ago",
                                "avg_price_m2_sale_started_2_quarters_ago",
                                "avg_price_m2_sale_started_1_quarter_ago",
                                "avg_price_m2_sale_started_now"
                            ]}
                            barUnit={` ${t("common.pln_m2")}`}
                            barMulticolorSet="alternate"
                            barFieldsLabels={quarterLabels}
                            roundValues
                            locked={isRegionIdReady && !isPermitted}
                            empty={!isRegionIdReady}
                        />
                    )}
                    <DistrictOsmMapCard
                        chartId="prices-map"
                        title={
                            <Trans>
                                {isKnownCity
                                    ? t("prices.map.title", {city: chartsCitySlug})
                                    : t("prices.map.title_region", {region: regionLabel})}
                            </Trans>
                        }
                        helper={t("prices.map.description")}
                        visible={districtsMapAvailable}
                        sticky
                        citySlug={chartsCitySlug}
                        regionId={regionId}
                        regionsMapSlug={osmMapParams.regionsMapSlug}
                        regionsMapParentId={osmMapParams.regionsMapParentId}
                        regionsMapChildType={osmMapParams.regionsMapChildType}
                        regionsMapRegionId={osmMapParams.regionsMapRegionId}
                        districtsMapAvailable={districtsMapAvailable}
                        districtValuesBySlug={districtPriceBySlug}
                        districtStatsCitySlug={districtStatsCitySlug}
                        legendType="pricing"
                        legendColors={["#F7D2FF", "#D0AEFF", "#9E86FF", "#826AF9", "#310AF5"]}
                        metricLabel={pricesMapMetricLabel}
                        locale={i18n.language}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        cityOutlineMetric={!showDistrictPolygons}
                        districtDrillDownEnabled={districtDrillDownEnabled}
                        onDistrictPolygonClick={handleDistrictPolygonClick}
                        onBackToParentRegion={mapParentRegion ? handleBackToMapParent : undefined}
                        backToParentLabel={mapParentRegion ? `← ${mapParentRegion.label}` : undefined}
                    />
                </Grid>
            </Layout>
        );
    })
);
